import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import Avatar from "@/components/ui/avatar";
import Chip from "@/components/ui/chip";
import ProgressBar from "@/components/ui/progress-bar";
import {
  Eye as EyeIcon,
  ArrowRightLong,
  Actions as ActionsIcon,
  Edit as EditIcon,
  EmployeeAdd,
  MapPin,
  Category,
  Book,
  Target,
  DocumentModal,
  Calendar,
} from "@/components/icons";
import type { Staff } from "../types/staff-management.types";
import styles from "./PersonnelSummaryDrawer.module.scss";
import Button from "@/components/ui/button";

interface PersonnelSummaryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  staff: Staff | null;
  onGoToDetail?: (staff: Staff) => void;
  onEdit?: (staff: Staff) => void;
  onEnrollInTraining?: (staff: Staff) => void;
}

// Extended display data (use API fields when available)
const getStaffDisplayData = (staff: Staff) => {
  const completedCount = staff.completedEducationsCount ?? 0;
  const totalTrainings =
    staff.takenEducationsCount ??
    completedCount;
  const certificateCount = staff.certificatesCount ?? 0;
  const absenteeismPresent = staff.absencesCount ?? 0;
  const absenteeismTotal =
    staff.takenEducationsCount ??
    absenteeismPresent;

  // Başarı ortalaması backend'den gelmiyor: hiç eğitim alınmamışken bile
  // "%85" göstermek mock değer izlenimi veriyor. completedCount=0 ise null,
  // aksi halde takenEducations üzerinden kaba bir oran hesaplıyoruz; backend
  // gerçek alanı eklediğinde buradan kaldırılır.
  const successAverage =
    completedCount > 0 && totalTrainings > 0
      ? Math.round((completedCount / totalTrainings) * 100)
      : null;

  return {
    ...staff,
    tcId: staff.identityNumber ?? "—",
    phone: staff.phoneNumber ?? "—",
    email: staff.email ?? "—",
    completedCount,
    totalTrainings,
    successAverage,
    certificateCount,
    absenteeismPresent,
    absenteeismTotal,
  };
};

const PersonnelSummaryDrawer: React.FC<PersonnelSummaryDrawerProps> = ({
  isOpen,
  onClose,
  staff,
  onGoToDetail,
  onEdit,
  onEnrollInTraining,
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  if (!staff) return null;

  const data = getStaffDisplayData(staff);
  const progressPercent =
    data.totalTrainings > 0
      ? Math.round((data.completedCount / data.totalTrainings) * 100)
      : 0;

  const headerTitle = (
    <span className={styles.headerTitle}>
      <EyeIcon width={24} height={24} color="#14181F" />
      {t("staffManagement.personnelSummary.title")}
    </span>
  );

  const headerActions = (
    <div className={styles.headerActions}>
      <Button
        type="button"
        variant="text"
        color="secondary"
        endIcon={<ArrowRightLong width={16} height={16} color="#707A8F" />}
        className={styles.detayButton}
        onClick={() => {
          onGoToDetail?.(staff);
          onClose();
        }}
      >
        {t("staffManagement.personnelSummary.goToDetail")}
      </Button>
      <div className={styles.menuWrapper} ref={menuRef}>
        <button
          type="button"
          className={styles.menuButton}
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-haspopup="true"
          aria-expanded={menuOpen}
        >
          <ActionsIcon width={20} height={20} />
        </button>
        {menuOpen && (
          <div className={styles.menuDropdown}>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                setMenuOpen(false);
                onEdit?.(staff);
                onClose();
              }}
            >
              <EditIcon width={18} height={18} />
              {t("staffManagement.actions.edit")}
            </button>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                setMenuOpen(false);
                onEnrollInTraining?.(staff);
                onClose();
              }}
            >
              <EmployeeAdd width={18} height={18} />
              {t("staffManagement.actions.enrollInTraining")}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={headerTitle}
      headerActions={headerActions}
      width="561px"
      position="right"
    >
      <div className={styles.drawerContent}>
        <div className={styles.personnelCard}>
          <div className={styles.personnelCardTop}>
            <div className={styles.avatarWrap}>
              <Avatar alt={staff.name} color="primary" />
            </div>
            <div className={styles.personnelInfo}>
              <div className={styles.personnelHeader}>
                <div className={styles.personnelNameTitle}>
                  <h2 className={styles.personnelName}>{staff.name}</h2>
                  <p className={styles.personnelTitle}>{staff.title}</p>
                </div>
                <div className={styles.badgesRow}>
                  <Chip type="success" size="small">
                    {t("staffManagement.status.active")}
                  </Chip>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.contactPills}>
            <span className={styles.contactPill}>TC {data.tcId}</span>
            <span className={styles.contactPill}>{data.phone}</span>
            <span className={styles.contactPill}>{data.email}</span>
          </div>
          <div className={styles.cardEducationProgress}>
            <h4 className={styles.cardEducationProgressTitle}>
              {t("staffManagement.personnelSummary.educationProgress")}
            </h4>
            <ProgressBar
              value={progressPercent}
              score={data.completedCount}
              total={data.totalTrainings}
              showLabel={false}
              size="medium"
            />
            <div className={styles.progressLabels}>
              <span>
                ({data.completedCount}/{data.totalTrainings})
              </span>
              <span className={styles.progressPercent}>
                {data.successAverage != null ? `%${data.successAverage}` : "—"}
              </span>
            </div>
          </div>
        </div>

        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            {t("staffManagement.personnelSummary.corporateInfo")}
          </h4>
          <div className={styles.sectionCard}>
            <div className={styles.detailList}>
              <div className={styles.detailItem}>
                <span className={styles.detailIcon}>
                  <MapPin width={20} height={20} />
                </span>
                <span className={styles.detailLabel}>
                  {t("staffManagement.personnelSummary.location")}
                </span>
                <span className={styles.detailValue}>{staff.location}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailIcon}>
                  <Category width={20} height={20} />
                </span>
                <span className={styles.detailLabel}>
                  {t("staffManagement.personnelSummary.department")}
                </span>
                <span className={styles.detailValue}>{staff.department}</span>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            {t("staffManagement.personnelSummary.educationInfo")}
          </h4>
          <div className={styles.sectionCard}>
            <div className={styles.detailList}>
              <div className={styles.detailItem}>
                <span className={styles.detailIcon} aria-hidden>
                  <Book width={20} height={20} color="#707a8f" />
                </span>
                <span className={styles.detailLabel}>
                  {t("staffManagement.personnelSummary.completedTrainings")}
                </span>
                <span className={styles.detailValue}>
                  {data.completedCount} / {data.totalTrainings}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailIcon} aria-hidden>
                  <Target width={20} height={20} color="#707a8f" />
                </span>
                <span className={styles.detailLabel}>
                  {t("staffManagement.personnelSummary.successAverage")}
                </span>
                <span className={styles.detailValue}>
                  {data.successAverage != null ? `%${data.successAverage}` : "—"}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailIcon} aria-hidden>
                  <DocumentModal width={20} height={20} color="#707a8f" />
                </span>
                <span className={styles.detailLabel}>
                  {t("staffManagement.personnelSummary.certificateCount")}
                </span>
                <span className={styles.detailValue}>
                  {data.certificateCount}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailIcon} aria-hidden>
                  <Calendar width={20} height={20} color="#707a8f" />
                </span>
                <span className={styles.detailLabel}>
                  {t("staffManagement.personnelSummary.absenteeism")}
                </span>
                <span className={styles.detailValue}>
                  {data.absenteeismPresent} / {data.absenteeismTotal}
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Drawer>
  );
};

export default PersonnelSummaryDrawer;
